import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Upload, FileText, Check, X, Lock } from 'lucide-react'
import { profileAPI } from '../services/api'
import { useI18n } from '../i18n'
import { fitsGatewayAsDataUrlJson } from '../utils/uploadLimit'
import { OrellaSpinner } from './OrellaLoader' // s123: the app's one spinner

// ★★★ THE KEEPER. TEST 1.04, decided 2026-09-21. Medical-record import exists twice; the
//   other implementation is src/pages/profile/ImportMedicalRecordsScreen.jsx (747 lines at
//   /profile/import-records, linked from nothing), and the full decision with its reasons
//   is written at the top of that file.
// ★★ WHY THIS ONE: it does not write the profile. It extracts, lets the patient tick what
//   they want, and hands the chosen subset to its OWNER through onDataExtracted — so there
//   is one write path for `conditions` and it lives in ProfileScreen. Do not move a
//   profileAPI write or a store setter into this component; that is the shape TEST 1.03
//   was filed against.
export default function MedicalDocumentUpload({ onDataExtracted, onClose }) {
  const { t } = useI18n()
  const [file, setFile] = useState(null)
  const [processing, setProcessing] = useState(false)
  const [extractedData, setExtractedData] = useState(null)
  const [error, setError] = useState(null)
  const [selectedItems, setSelectedItems] = useState({})
  const [visible, setVisible] = useState(false)
  const fileInputRef = useRef(null)

  // Animate in
  useEffect(() => {
    requestAnimationFrame(() => requestAnimationFrame(() => setVisible(true)))
  }, [])

  const handleClose = () => {
    setVisible(false)
    setTimeout(onClose, 350)
  }

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files?.[0]
    if (!selectedFile) return
    if (selectedFile.size > 4 * 1024 * 1024) {
      setError(t('errors2.fileTooLarge'))
      return
    }
    // ★ PROD-228: the request carries the file as base64 (4/3 of it), so a file under the
    //   size above can still be over Lambda's 6 MB request quota (uploadLimit.js); AWS
    //   refuses that before orella-api runs, with no CORS, and it reads as a network error.
    if (!fitsGatewayAsDataUrlJson(selectedFile)) {
      setError(t('detect.uploadFailedTooLarge', { name: selectedFile.name, defaultValue: '"{{name}}" is too large to upload.' }))
      return
    }
    setFile(selectedFile)
    setError(null)
    setExtractedData(null)
  }

  const processDocument = async () => {
    if (!file) return
    setProcessing(true)
    setError(null)

    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result)
        reader.onerror = reject
        reader.readAsDataURL(file)
      })

      const response = await profileAPI.parseMedicalDocument({
        file: base64,
        fileType: file.type,
        fileName: file.name
      })

      if (response.success && response.data) {
        setExtractedData(response.data)
        const selections = {}
        response.data.allergies?.forEach((_, i) => selections[`allergy_${i}`] = true)
        response.data.medications?.forEach((_, i) => selections[`med_${i}`] = true)
        response.data.medicalHistory?.forEach((_, i) => selections[`history_${i}`] = true)
        response.data.familyHistory?.forEach((_, i) => selections[`family_${i}`] = true)
        if (response.data.socialHistory) selections['social'] = true
        setSelectedItems(selections)
      } else {
        setError(t('errors2.failedToExtract'))
      }
    } catch (err) {
      console.error('Error:', err)
      setError(t('errors2.failedToProcess'))
    }
    setProcessing(false)
  }

  const handleSave = () => {
    if (!extractedData) return
    const data = { allergies: [], medications: [], medicalHistory: [], familyHistory: [], socialHistory: null }
    extractedData.allergies?.forEach((item, i) => { if (selectedItems[`allergy_${i}`]) data.allergies.push(item) })
    extractedData.medications?.forEach((item, i) => { if (selectedItems[`med_${i}`]) data.medications.push(item) })
    extractedData.medicalHistory?.forEach((item, i) => { if (selectedItems[`history_${i}`]) data.medicalHistory.push(item) })
    extractedData.familyHistory?.forEach((item, i) => { if (selectedItems[`family_${i}`]) data.familyHistory.push(item) })
    if (selectedItems['social']) data.socialHistory = extractedData.socialHistory
    onDataExtracted(data)
  }

  const toggleItem = (key) => setSelectedItems(prev => ({ ...prev, [key]: !prev[key] }))

  const getItemCount = () => {
    let count = 0
    if (extractedData) {
      extractedData.allergies?.forEach((_, i) => { if (selectedItems[`allergy_${i}`]) count++ })
      extractedData.medications?.forEach((_, i) => { if (selectedItems[`med_${i}`]) count++ })
      extractedData.medicalHistory?.forEach((_, i) => { if (selectedItems[`history_${i}`]) count++ })
      extractedData.familyHistory?.forEach((_, i) => { if (selectedItems[`family_${i}`]) count++ })
    }
    return count
  }
  const selectedCount = getItemCount()

  const SUPPORTED_TAGS = [
    t('profileMain.dischargeSummaries'),
    t('profileMain.medicationLists'),
    t('profileMain.labResults'),
    t('profileMain.clinicalNotes'),
  ]

  const renderCheckbox = (key) => (
    <div
      className="rounded flex items-center justify-center flex-shrink-0"
      style={{ width: 18, height: 18, background: selectedItems[key] ? '#22C55E' : '#E5E7EB' }}
    >
      {selectedItems[key] && <Check size={12} className="text-white" strokeWidth={3} />}
    </div>
  )

  const renderSection = (title, items, keyPrefix, renderLabel) => {
    if (!items?.length) return null
    return (
      <div className="border border-gray-200 rounded-xl mb-2.5 overflow-hidden">
        <div className="bg-gray-50 px-3 py-2 text-[13px] font-semibold" style={{ color: '#15354F' }}>{title}</div>
        {items.map((item, i) => {
          const key = `${keyPrefix}_${i}`
          return (
            <button
              key={i}
              onClick={() => toggleItem(key)}
              className="w-full px-3 py-2.5 text-left text-[13px] border-t border-gray-100 flex items-center gap-2"
              style={{ opacity: selectedItems[key] ? 1 : 0.4, textDecoration: selectedItems[key] ? 'none' : 'line-through' }}
            >
              {renderCheckbox(key)}
              <span className="flex-1">{renderLabel(item)}</span>
            </button>
          )
        })}
      </div>
    )
  }

  return createPortal(
    <div className="fixed inset-0 z-[9999]">
      {/* Backdrop */}
      <div
        className="absolute inset-0 transition-opacity duration-300"
        style={{ backgroundColor: visible ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0)' }}
        onClick={handleClose}
      />

      {/* Bottom Sheet */}
      <div
        className="absolute bottom-0 left-0 right-0 bg-white flex flex-col transition-transform duration-300"
        style={{
          borderRadius: '24px 24px 0 0',
          maxHeight: '85dvh',
          transform: visible ? 'translateY(0)' : 'translateY(100%)',
          transitionTimingFunction: 'cubic-bezier(0.32, 0.72, 0, 1)',
        }}
      >
        {/* Handle */}
        <div className="flex justify-center pt-2.5 pb-1">
          <div className="w-9 h-1 rounded-full bg-gray-300" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-2 pb-2">
          <div className="flex items-center gap-2">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#46A8F9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" />
            </svg>
            <h2 className="text-[17px] font-extrabold" style={{ color: '#15354F' }}>
              {t('profileMain.autoFillProfile')}
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center"
          >
            <X size={14} className="text-gray-500" />
          </button>
        </div>

        {/* Subtitle */}
        <p className="px-5 pb-4 text-[13px] text-gray-500 leading-relaxed">
          {t('profileMain.autoFillSubtitle')}
        </p>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-5 pb-6">
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,image/*"
            onChange={handleFileSelect}
            className="hidden"
          />

          {!extractedData ? (
            <>
              {/* Upload zone */}
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full rounded-2xl border-2 border-dashed text-center active:scale-[0.98] transition-all"
                style={{
                  padding: '28px 20px',
                  borderColor: file ? '#22C55E' : '#D1D5DB',
                  backgroundColor: file ? '#F0FDF4' : 'white',
                }}
              >
                {file ? (
                  <>
                    <div className="w-[52px] h-[52px] rounded-2xl bg-green-50 flex items-center justify-center mx-auto mb-3">
                      <FileText size={24} className="text-green-500" />
                    </div>
                    <p className="text-[15px] font-bold" style={{ color: '#15354F' }}>{file.name}</p>
                    <p className="text-[12px] text-gray-400 mt-1">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                  </>
                ) : (
                  <>
                    <div className="w-[52px] h-[52px] rounded-2xl flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: '#E9F8FF' }}>
                      <Upload size={24} style={{ color: '#46A8F9' }} />
                    </div>
                    <p className="text-[15px] font-bold" style={{ color: '#15354F' }}>{t('profileMain.tapToSelectDocument')}</p>
                    <p className="text-[12px] text-gray-400 mt-1">{t('profileMain.pdfOrPhotoMax10mb')}</p>
                  </>
                )}
              </button>

              {error && (
                <div className="mt-3 p-3 bg-red-50 text-red-600 rounded-xl text-[13px]">
                  {error}
                </div>
              )}

              {/* Privacy note */}
              <div className="mt-3.5 flex items-start gap-2 px-3.5 py-3 bg-gray-50 rounded-xl">
                <Lock size={14} className="text-gray-400 flex-shrink-0 mt-0.5" />
                <p className="text-[11px] text-gray-400 leading-relaxed">
                  {t('profileMain.documentPrivacyNote')}
                </p>
              </div>

              {/* Supported formats */}
              <div className="mt-3.5 px-4 py-3.5 bg-gray-50 rounded-xl">
                <p className="text-[11px] font-semibold text-gray-500 mb-2">{t('profileMain.worksBestWith')}</p>
                <div className="flex flex-wrap gap-1.5">
                  {SUPPORTED_TAGS.map((tag, i) => (
                    <span key={i} className="text-[10px] font-semibold px-2.5 py-1 rounded-full bg-white text-gray-500 border border-gray-200">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Extract button — only shown when file selected */}
              {file && (
                <button
                  onClick={processDocument}
                  disabled={processing}
                  className="w-full mt-4 py-3.5 rounded-2xl text-white font-bold text-[15px] flex items-center justify-center gap-2 active:scale-[0.97] transition-all disabled:opacity-50"
                  style={{ backgroundColor: '#15354F', boxShadow: '0 8px 20px rgba(21,53,79,0.25)' }}
                >
                  {processing ? (
                    <>
                      <OrellaSpinner size={18} tone="inherit" />
                      {t('common.analyzing', 'Analyzing...')}
                    </>
                  ) : (
                    <>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" />
                      </svg>
                      {t('importRecords.extractInformation', 'Extract Information')}
                    </>
                  )}
                </button>
              )}
            </>
          ) : (
            <>
              {/* Extracted data review */}
              <div className="p-3 bg-green-50 text-green-800 rounded-xl text-[13px] font-medium mb-3">
                ✓ Found {selectedCount} items. Tap to deselect any you don't want.
              </div>

              {renderSection('Allergies', extractedData.allergies, 'allergy',
                (item) => `${item.substance}${item.reaction ? ` — ${item.reaction}` : ''}`
              )}
              {renderSection('Medications', extractedData.medications, 'med',
                (item) => `${item.name} ${item.dosage || ''} ${item.frequency || ''}`.trim()
              )}
              {renderSection('Medical History', extractedData.medicalHistory, 'history',
                (item) => `${item.condition || item.name}${item.year ? ` (${item.year})` : ''}`
              )}
              {renderSection('Family History', extractedData.familyHistory, 'family',
                (item) => `${item.condition}${item.relation ? ` (${item.relation})` : ''}`
              )}

              {extractedData.socialHistory && (
                <div className="border border-gray-200 rounded-xl mb-2.5 overflow-hidden">
                  <div className="bg-gray-50 px-3 py-2 text-[13px] font-semibold" style={{ color: '#15354F' }}>{t('profileMain.socialAndLifestyle', 'Social & Lifestyle')}</div>
                  <button
                    onClick={() => toggleItem('social')}
                    className="w-full px-3 py-2.5 text-left text-[13px] border-t border-gray-100 flex items-center gap-2"
                    style={{ opacity: selectedItems['social'] ? 1 : 0.4, textDecoration: selectedItems['social'] ? 'none' : 'line-through' }}
                  >
                    {renderCheckbox('social')}
                    <span className="flex-1">
                      {[
                        extractedData.socialHistory.tobacco && `Tobacco: ${extractedData.socialHistory.tobacco}`,
                        extractedData.socialHistory.alcohol && `Alcohol: ${extractedData.socialHistory.alcohol}`,
                        extractedData.socialHistory.occupation,
                      ].filter(Boolean).join(' · ')}
                    </span>
                  </button>
                </div>
              )}

              {/* Action buttons */}
              <div className="mt-3 space-y-2">
                <button
                  onClick={handleSave}
                  disabled={selectedCount === 0}
                  className="w-full py-3.5 rounded-2xl text-white font-bold text-[15px] flex items-center justify-center gap-2 active:scale-[0.97] transition-all disabled:opacity-40"
                  style={{ backgroundColor: '#15354F', boxShadow: selectedCount > 0 ? '0 8px 20px rgba(21,53,79,0.25)' : 'none' }}
                >
                  <Check size={18} />
                  {t('profileMain.import', 'Import')} {selectedCount} {selectedCount === 1 ? 'Item' : 'Items'}
                </button>

                <button
                  onClick={() => { setExtractedData(null); setFile(null); setError(null) }}
                  className="w-full py-2.5 text-[13px] font-medium text-gray-400"
                >
                  {t('profileMain.tryDifferentDocument')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}
